import {
  CheckmarkFill18Icon,
  CloseFill18Icon,
} from "@vygruppen/spor-icon-react";
// eslint-disable-next-line drops/no-direct-spor-import
import { Box, ChoiceChip } from "@vygruppen/spor-react";
import { ComponentProps, FC, isValidElement, ReactNode } from "react";
import styled from "styled-components";

type NativeChoiceChipIcon = NonNullable<
  ComponentProps<typeof ChoiceChip>["icon"]
>;

type ChoiceChipProps = Omit<ComponentProps<typeof ChoiceChip>, "icon"> & {
  icon?: NativeChoiceChipIcon | ReactNode;
};

// Has a checkmark when checked
type FilterChipProps = Omit<ChoiceChipProps, "icon">;

type ChipAdornmentProps = {
  leadingIcon?: ReactNode;
  checkedLeadingIcon?: ReactNode;
  checkedTrailingIcon?: ReactNode;
};

const StyledChoiceChip = styled(ChoiceChip)<{
  $hasStartIcon: boolean;
  $hasEndIcon: boolean;
}>`
  &&&:focus-visible,
  &&&[data-focus-visible] {
    outline: 1px solid ${({ theme }) => theme.colorTextDisabled};
    outline-offset: 0px;
  }
  transition: none;

  &&& {
    border-radius: 6px;
    padding-inline-start: ${({ $hasStartIcon }) =>
      $hasStartIcon ? "0px" : "15px"};
    padding-inline-end: ${({ $hasEndIcon }) => ($hasEndIcon ? "0px" : "15px")};

    &:not([data-disabled]) {
      color: ${({ theme }) => theme.colorTextMain};

      &[data-scope="checkbox-card"] [data-part="content"],
      &[data-scope="checkbox-card"] [data-part="label"] {
        color: ${({ theme }) => theme.colorTextMain};
      }
    }

    span {
      font-weight: normal;
    }

    &[data-state="checked"] {
      background-color: ${({ theme }) => theme.colorSelected};
      border: none;
      outline: none;
    }
    &[data-disabled] {
      border: none;
      outline: none;
      color: ${({ theme }) => theme.colorTextDisabled};

      &[data-scope="checkbox-card"] [data-part="content"],
      &[data-scope="checkbox-card"] [data-part="label"] {
        color: ${({ theme }) => theme.colorTextDisabled};
      }
    }

    &[data-state="checked"][data-disabled] {
      background-color: ${({ theme }) => theme.surfaceDisabled};
      border: none;
      outline: none;
    }
  }
`;

type ResolvedIcon = {
  nativeIcon: NativeChoiceChipIcon | undefined;
  customIcon: ReactNode;
};

const resolveIcon = (icon: ChoiceChipProps["icon"]): ResolvedIcon => {
  const isNativeChoiceChipIcon =
    typeof icon === "object" &&
    icon !== null &&
    !isValidElement(icon) &&
    "default" in icon &&
    "checked" in icon;

  return {
    nativeIcon: isNativeChoiceChipIcon ? icon : undefined,
    customIcon: isNativeChoiceChipIcon ? undefined : icon,
  };
};

const createChoiceChip = ({
  leadingIcon,
  checkedLeadingIcon,
  checkedTrailingIcon,
}: ChipAdornmentProps = {}) => {
  const Component: FC<ChoiceChipProps> = ({
    checked,
    children,
    disabled,
    icon,
    ...props
  }) => {
    const { nativeIcon, customIcon } = resolveIcon(icon);
    const checkedIcon = checked ? checkedLeadingIcon : null;
    const trailingIcon = checked ? checkedTrailingIcon : null;
    const hasStartIcon = Boolean(
      nativeIcon || customIcon || leadingIcon || checkedIcon,
    );
    const hasEndIcon = Boolean(trailingIcon);

    return (
      <StyledChoiceChip
        checked={checked}
        disabled={disabled}
        $hasStartIcon={hasStartIcon}
        $hasEndIcon={hasEndIcon}
        icon={nativeIcon}
        {...props}
      >
        {leadingIcon ? <Box ml="8px">{leadingIcon}</Box> : null}
        {!nativeIcon && !customIcon ? checkedIcon : null}
        {customIcon ? <Box ml="8px">{customIcon}</Box> : null}
        {children}
        {trailingIcon}
      </StyledChoiceChip>
    );
  };

  return Component;
};

export const DropsChoiceChip = createChoiceChip();

const FilterChipComponent = createChoiceChip({
  checkedLeadingIcon: <CheckmarkFill18Icon ml="4px" />,
});

export const DropsFilterChip: FC<FilterChipProps> = (props) => (
  <FilterChipComponent {...props} />
);

export const DropsInputChip = createChoiceChip({
  checkedTrailingIcon: <CloseFill18Icon mr="4px" />,
});
